import * as React from 'react'

import { DocumentNode, useApolloClient } from '@apollo/client'

import { EventTypes, subscribe, unsubscribe } from 'modules/core/lib/pubSub'
import type { Event } from 'modules/core/lib/pubSub/types'

export type OneMutationNotification = {
  operationName: string
  variables?: Record<string, unknown>
  result?: unknown
}

/**
 * Mastra mutation payloads are emitted into the frontend as generic `unknown` data because the
 * corresponding mutation documents and generated result types may exist only in the Mastra repo,
 * not in factorial-frontend. We still need to safely read a few nested fields from that payload
 * without falling back to `any` or copying large hand-written types into each domain.
 *
 * Example:
 * const employeeId = getNestedString(payload.result, [
 *   'timeoffMutations',
 *   'createTimeoffLeave',
 *   'leave',
 *   'employee',
 *   'id',
 * ])
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function getNestedValue(value: unknown, path: readonly string[]): unknown {
  let current: unknown = value

  for (const key of path) {
    if (!isRecord(current) || !(key in current)) {
      return undefined
    }

    current = current[key]
  }

  return current
}

export function getNestedString(value: unknown, path: readonly string[]): string | undefined {
  const nestedValue = getNestedValue(value, path)

  return typeof nestedValue === 'string' ? nestedValue : undefined
}

type OneMutationSubscriptionHandler = (
  payload: OneMutationNotification,
  apolloClient: ReturnType<typeof useApolloClient>
) => void | Promise<void>

function isDocumentNode(value: unknown): value is DocumentNode {
  return typeof value === 'object' && value !== null && 'kind' in value && value.kind === 'Document'
}

function hasOperationName(value: Record<string, unknown>): value is OneMutationNotification {
  return typeof value.operationName === 'string'
}

function isOneMutationNotification(data: unknown): data is OneMutationNotification {
  return isRecord(data) && hasOperationName(data)
}

function isActiveQuery(
  apolloClient: ReturnType<typeof useApolloClient>,
  query: DocumentNode
): boolean {
  return Array.from(apolloClient.getObservableQueries('active').values()).some(
    (observableQuery) => observableQuery.query === query
  )
}

export function useOneMutationSubscription(
  operationName: string,
  handler: OneMutationSubscriptionHandler,
  fromObservable?: boolean
): void

export function useOneMutationSubscription(
  operationName: string,
  query: DocumentNode,
  fromObservable?: boolean
): void

export function useOneMutationSubscription(
  operationName: string,
  handlerOrQuery: OneMutationSubscriptionHandler | DocumentNode,
  fromObservable = false
) {
  const apolloClient = useApolloClient()

  React.useEffect(() => {
    const handler: OneMutationSubscriptionHandler = isDocumentNode(handlerOrQuery)
      ? async (_payload, client) => {
          if (!fromObservable && !isActiveQuery(client, handlerOrQuery)) {
            return
          }

          await client.refetchQueries({ include: [handlerOrQuery] })
        }
      : handlerOrQuery

    const handleMutationExecuted = (event: Event) => {
      const payload = event.data

      if (!isOneMutationNotification(payload) || payload.operationName !== operationName) {
        return
      }

      void (async () => {
        try {
          await handler(payload, apolloClient)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Error while handling ONE_MUTATION_EXECUTED subscription event',
            { operationName: payload.operationName },
            error
          )
        }
      })()
    }

    subscribe(EventTypes.ONE_MUTATION_EXECUTED, handleMutationExecuted)

    return () => {
      unsubscribe(handleMutationExecuted)
    }
  }, [apolloClient, handlerOrQuery, operationName, fromObservable])
}
